<script setup lang="ts">
import { ref, watch, nextTick, watchEffect, computed } from "vue";
import { useRouter } from "vue-router";
import type { AxiosError } from "axios";
import { useAuthStore } from "@/stores/authStore";
import AnalysisService from "@/services/analysis/analysisService";

import BottomSheet from "@/components/BottomSheet.vue";
import ScanBottomSheet from "@/components/ScanBottomSheet.vue";
import ValidationBottomSheet from "@/components/ValidationBottomSheet.vue";
import PricingBottomSheet from "@/components/PricingBottomSheet.vue";
import ManualSelectionBottomSheet from "@/components/ManualSelectionBottomSheet.vue";

import type {
  AnalysisResult,
  AnalyzeStreamEvent,
  NameplateAnalysisData,
  RepairRequestForm,
} from "@/types/services/analysis/analysis.type";
import type { ApiError } from "@/types/services/utils/axios.type";
import type {
  PricingOption,
  ScanErrorState,
  ScanErrorType,
  Step,
} from "@/types/views/captureView.type";
import { useI18n } from "vue-i18n";
import RepairRequestBottomSheet from "@/components/RepairRequestBottomSheet.vue";
import { displayResponseError, displayToast } from "@/utils/toastUtils.ts";
import {
  FAILED_STEP_KEYS,
  INVALID_IMAGE_REASONS,
  PIPELINE_ERROR_REASONS,
} from "@/constants/views/captureViewConstant";

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();

const step = ref<Step>("scan");
const capturedImage = ref<string | null>(null);
const capturedBlob = ref<Blob | null>(null);
const analysisResult = ref<AnalysisResult | null>(null);
const selectedOption = ref<PricingOption>(null);
const isSubmittingValidation = ref(false);
const isAnalyzing = ref(false);
const analysisProgressMessage = ref<string | null>(null);

const scanError = ref<ScanErrorState | null>(null);

const previewHeight = ref<number | null>(null);
const bottomSheetRef = ref<InstanceType<typeof BottomSheet> | null>(null);
const bottomSheetHeight = ref(0);

const galleryInputRef = ref<HTMLInputElement | null>(null);
const cameraInputRef = ref<HTMLInputElement | null>(null);

const stepComponentMap = {
  scan: ScanBottomSheet,
  validation: ValidationBottomSheet,
  manualSelection: ManualSelectionBottomSheet,
  tarification: PricingBottomSheet,
  repairRequest: RepairRequestBottomSheet,
};

const currentComponent = computed(() => stepComponentMap[step.value]);

const scanOverlayHeight = computed(() =>
  step.value === "scan"
    ? `calc(100% - ${bottomSheetHeight.value}px + 50px)`
    : "100%",
);

const resolveFailedStepLabel = (failedStep: string | null): string | null => {
  if (!failedStep) {
    return null;
  }

  const key = FAILED_STEP_KEYS[failedStep];
  return key ? t(key) : failedStep;
};

const setScanError = (
  type: ScanErrorType,
  reasonCode?: string | null,
  failedStep?: string | null,
  fallbackMessage?: string,
) => {
  const failedStepLabel = resolveFailedStepLabel(failedStep ?? null);

  if (type === "invalid_image") {
    scanError.value = {
      type,
      title: t("views.captureView.scanError.invalidImage.title"),
      description: t(
        `views.captureView.scanError.invalidImage.reasons.${reasonCode ?? "OTHER_REASON"}`,
      ),
    };
    return;
  }

  if (type === "analysis_failed") {
    const translatedReason = t(
      `views.captureView.scanError.analysisFailed.reasons.${reasonCode ?? "UNKNOWN_ERROR"}`,
    );

    scanError.value = {
      type,
      title: t("views.captureView.scanError.analysisFailed.title"),
      description: fallbackMessage
        ? fallbackMessage
        : failedStepLabel
          ? t(
              "views.captureView.scanError.analysisFailed.descriptionWithStep",
              {
                reason: translatedReason,
                step: failedStepLabel,
              },
            )
          : t("views.captureView.scanError.analysisFailed.description", {
              reason: translatedReason,
            }),
    };
    return;
  }

  scanError.value = {
    type,
    title: t("views.captureView.scanError.generic.title"),
    description:
      fallbackMessage ?? t("views.captureView.scanError.generic.description"),
  };
};

const handleStreamEvent = (event: AnalyzeStreamEvent): void => {
  if (event.type === "progress") {
    analysisProgressMessage.value = event.message;
  }
};

const isFailedAnalyzeResult = (
  result: Awaited<ReturnType<typeof AnalysisService.analyze>>,
): result is { type: "result"; status: "failed"; error: string } => {
  return "status" in result && result.status === "failed";
};

const handleResultError = (analysis: NameplateAnalysisData): void => {
  const reasonCode = analysis.invalid_reason;
  if (!reasonCode) {
    setScanError("generic");
    return;
  }

  if (
    INVALID_IMAGE_REASONS.includes(
      reasonCode as (typeof INVALID_IMAGE_REASONS)[number],
    )
  ) {
    setScanError("invalid_image", reasonCode, analysis.failed_step);
    return;
  }

  if (
    PIPELINE_ERROR_REASONS.includes(
      reasonCode as (typeof PIPELINE_ERROR_REASONS)[number],
    )
  ) {
    setScanError("analysis_failed", reasonCode, analysis.failed_step);
    return;
  }

  setScanError("generic");
};

const handleSheetHeightChange = (height: number) => {
  bottomSheetHeight.value = height;
};

watch(step, async () => {
  await nextTick();
  bottomSheetRef.value?.expandTo();
});

watchEffect(() => {
  bottomSheetRef.value?.expandTo();
});

const handleFileUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;

  try {
    isAnalyzing.value = true;
    scanError.value = null;
    analysisProgressMessage.value = null;
    const blob = new Blob([await file.arrayBuffer()], { type: file.type });
    const preview = URL.createObjectURL(blob);

    capturedBlob.value = blob;
    capturedImage.value = preview;

    await nextTick();

    previewHeight.value = window.innerHeight - bottomSheetHeight.value;

    const streamResult = await AnalysisService.analyze(
      { image: blob },
      handleStreamEvent,
    );

    if (isFailedAnalyzeResult(streamResult)) {
      setScanError(
        "analysis_failed",
        "UNKNOWN_ERROR",
        null,
        streamResult.error,
      );
      return;
    }

    const result = streamResult.data;
    const analysis = result.analysis;

    if (analysis.invalid_reason) {
      handleResultError(analysis);
      return;
    }

    analysisResult.value = result;
    step.value = "validation";

    displayToast(t("views.captureView.snackbar.identified"), "success");
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>);
    setScanError("generic");
  } finally {
    isAnalyzing.value = false;
    analysisProgressMessage.value = null;
    if (target) target.value = "";
  }
};

const resetScan = async () => {
  if (capturedImage.value?.startsWith("blob:")) {
    URL.revokeObjectURL(capturedImage.value);
  }

  capturedImage.value = null;
  capturedBlob.value = null;
  analysisResult.value = null;
  selectedOption.value = null;
  scanError.value = null;
  analysisProgressMessage.value = null;

  step.value = "scan";
  await nextTick();
  bottomSheetRef.value?.collapseTo();
};

const submitValidation = async (payload: {
  valid: boolean;
  serialNumber?: string;
}) => {
  if (!analysisResult.value || isSubmittingValidation.value) return;

  try {
    isSubmittingValidation.value = true;

    await AnalysisService.submitValidation({
      analysis: analysisResult.value,
      validated: payload.valid,
      serial_number: payload.serialNumber,
    });

    if (!payload.valid) {
      step.value = "manualSelection";
      return;
    }

    step.value = "tarification";
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>);
  } finally {
    isSubmittingValidation.value = false;
  }
};

const handleManualSelection = async (payload: {
  serial_number: string;
  brand_id: string;
  product_id: string;
}) => {
  try {
    isSubmittingValidation.value = true;

    await AnalysisService.submitManualSelection({
      analysis: analysisResult.value,
      ...payload,
    });

    step.value = "tarification";
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>);
  } finally {
    isSubmittingValidation.value = false;
  }
};

const validatePricing = async () => {
  if (!analysisResult.value || !selectedOption.value) return;

  try {
    isSubmittingValidation.value = true;

    await AnalysisService.submitPricingChoice({
      analysis: analysisResult.value,
      pricing_choice: selectedOption.value,
    });

    step.value = "repairRequest";
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>);
  } finally {
    isSubmittingValidation.value = false;
  }
};

const validateRepairRequest = async (payload: RepairRequestForm) => {
  if (
    !analysisResult.value ||
    !selectedOption.value ||
    !capturedBlob.value ||
    isSubmittingValidation.value
  )
    return;

  try {
    isSubmittingValidation.value = true;
    const emailCopy = localStorage.getItem("email");

    await AnalysisService.submitRepairRequest({
      analysis: analysisResult.value,
      pricing_choice: selectedOption.value,
      ...payload,
      email_copy: emailCopy || "",
      photo: capturedBlob.value,
    });

    await resetScan();
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>);
  } finally {
    isSubmittingValidation.value = false;
  }
};

const logout = async () => {
  await authStore.logout();
  await router.replace("/login");
};
</script>

<template>
  <v-app>
    <v-main class="capture-page">
      <div class="top-actions">
        <v-btn
          icon
          variant="flat"
          :disabled="isAnalyzing || isSubmittingValidation"
          :title="t('common.reset')"
          @click="resetScan"
        >
          <v-icon>PhArrowCounterClockwise</v-icon>
        </v-btn>
        <v-btn icon variant="flat" @click="logout">
          <v-icon>PhSignOut</v-icon>
        </v-btn>
      </div>

      <div v-if="capturedImage" class="preview-global">
        <div class="preview-container">
          <img
            :src="capturedImage"
            class="preview-image"
            :style="
              previewHeight ? { height: `${previewHeight}px` } : undefined
            "
            alt=""
          />
        </div>
      </div>

      <div
        v-if="step === 'scan' && !capturedImage"
        class="upload-overlay"
        :style="{ height: scanOverlayHeight }"
      >
        <div class="upload-card upload-card--primary">
          <div class="upload-icon">
            <v-icon size="28">PhUpload</v-icon>
          </div>

          <div class="upload-content">
            <h3>
              {{ t("views.captureView.downloadPhoto") }}
            </h3>
            <p>{{ t("views.captureView.fromDevice") }}</p>
          </div>

          <v-btn
            class="upload-action btn-primary"
            rounded="xl"
            height="44"
            @click="galleryInputRef?.click()"
          >
            {{ t("views.captureView.choose") }}
          </v-btn>
        </div>

        <div class="divider">
          <span></span>
          <div class="divider-text">{{ t("views.captureView.or") }}</div>
          <span></span>
        </div>

        <div class="upload-card upload-card--secondary">
          <div class="upload-icon">
            <v-icon size="28">PhCamera</v-icon>
          </div>

          <div class="upload-content">
            <h3>{{ t("views.captureView.takePicture") }}</h3>
            <p>
              {{ t("views.captureView.backCamera") }}
            </p>
          </div>

          <v-btn
            class="upload-action btn-primary"
            rounded="xl"
            height="44"
            @click="cameraInputRef?.click()"
          >
            {{ t("views.captureView.open") }}
          </v-btn>
        </div>
      </div>

      <input
        ref="galleryInputRef"
        type="file"
        accept="image/*"
        style="display: none"
        @change="handleFileUpload"
      />

      <input
        ref="cameraInputRef"
        type="file"
        accept="image/*"
        capture="environment"
        style="display: none"
        @change="handleFileUpload"
      />

      <BottomSheet
        ref="bottomSheetRef"
        :initial="step === 'scan' ? 'collapsed' : 'expanded'"
        @heightChange="handleSheetHeightChange"
      >
        <component
          :is="currentComponent"
          :analysis="analysisResult"
          :disabled="step === 'tarification' && !analysisResult"
          :is-analyzing="isAnalyzing"
          :loading="isSubmittingValidation"
          :progress-message="analysisProgressMessage"
          :scan-error="scanError"
          v-model:selectedOption="selectedOption"
          @validate="submitValidation"
          @confirmPricing="validatePricing"
          @confirm="handleManualSelection"
          @confirmRepair="validateRepairRequest"
          @retry="resetScan"
        />
      </BottomSheet>
    </v-main>
  </v-app>
</template>

<style>
.upload-overlay {
  position: absolute;
  inset: 0;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  background: url("@/assets/login-bg.jpg") center / cover no-repeat;
}

.upload-card {
  width: 100%;
  max-width: 480px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 50px 40px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
}

.upload-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(106, 168, 32, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #559a1b;
  flex-shrink: 0;
}

.upload-content {
  flex: 1;
}

.upload-content h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.upload-content p {
  margin: 2px 0 0;
  font-size: 13px;
  opacity: 0.65;
}

.upload-action {
  background: #6aa820;
  color: white;
  font-weight: 600;
  padding: 0 18px;
}

.divider {
  width: 100%;
  max-width: min(520px, 92vw);
  text-align: center;
  opacity: 1;
  color: white;
  font-size: 22px;
  font-weight: bold;
}

.hint {
  font-size: 12px;
  opacity: 0.6;
  margin-top: 8px;
}

.preview-wrapper {
  width: 100%;
  max-width: min(520px, 92vw);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.preview-image {
  width: 100%;
  object-fit: cover;
  background: white;
}
.divider {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 360px;
}

.divider span {
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 1);
}

.divider-text {
  flex: none;
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-transform: lowercase;
}
</style>
